import React, { useEffect, useState, useRef } from "react";
import { observer, inject } from "mobx-react";
import { Route, Routes, useLocation } from "react-router-dom";

import Header from "../Components/Header";
import Rail from "../Components/Rail";
import Playlist from "../Components/Playlist";
import AddToPlaylistModal from "../Components/AddToPlaylistModal";
import Home from "./Home";
import MyProfile from "./MyProfile";
import Admin from "./Admin";
import UserProfile from "./UserProfile";
import Playback from "./Playback";
import Dj from "./Dj";
import DjQueuePanel from "./Dj/DjQueuePanel";
import { UploadContextProvider } from "../Contexts/UploadContext";
import { pauseAllAndCancel as stopDjAudio } from "../Utils/djAudioController";

function Main({ PlaylistStore, RecentUsersStore, PlaybackStore, DjStore }) {
  const location = useLocation();
  const djMode = location.pathname.startsWith("/app/dj");

  useEffect(() => {
    if (!djMode) return;
    if (PlaybackStore.isPlaying) PlaybackStore.pause?.();
    const el = document.getElementById("DOMAudioPlayer");
    if (el && !el.paused) el.pause();
  }, [djMode, PlaybackStore]);

  // The DJ queue is its own accumulating collection, fed explicitly from the
  // queue panel — it starts empty each time DJ mode is entered. The crowd-submit
  // feed polls while in DJ mode (links persist on the server across sessions).
  useEffect(() => {
    if (!djMode) return;
    DjStore.clearQueue();
    DjStore.startSubmissionFeed();
    return () => DjStore.stopSubmissionFeed();
  }, [djMode, DjStore]);
  const [playlistVisible, setPlaylistVisible] = useState(false);
  const playlistRef = useRef(null);

  useEffect(() => {
    window.PlaylistStore = PlaylistStore;
    PlaylistStore.onLoadPlaylist((playlist) => {
      PlaybackStore.setPlaylist(playlist, PlaylistStore.currentViewingPlaylistId);
      PlaylistStore.clear();
    });
    PlaylistStore.loadPlaylist();
    PlaylistStore.loadUserPlaylists(); // Load user's playlists globally
    RecentUsersStore.load();

    // Restore playback state from server (resume across devices)
    PlaybackStore.restoreFromServer();

    const recentUserInterval = setInterval(
      () => RecentUsersStore.load(),
      300000
    );

    // Keyboard controls
    let seekInterval = 5; // Initial skip interval in seconds
    let seekTimer = null;
    let currentKey = null;

    const handleKeyDown = (e) => {
      // Ignore if typing in input/textarea
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') {
        return;
      }

      // Spacebar: Toggle play/pause
      if (e.key === ' ' && document.activeElement === document.querySelector('body')) {
        PlaybackStore.togglePlay();
        e.preventDefault();
        return;
      }

      // Arrow keys: Seek left/right
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        e.preventDefault();

        const audioElement = document.getElementById('DOMAudioPlayer');
        if (!audioElement || !audioElement.duration) return;

        const direction = e.key === 'ArrowLeft' ? -1 : 1;

        // If this is a new key press (not holding)
        if (currentKey !== e.key) {
          currentKey = e.key;
          seekInterval = 5; // Reset interval

          // Initial skip
          const currentTime = audioElement.currentTime;
          const duration = audioElement.duration;
          const newTime = Math.max(0, Math.min(duration, currentTime + (direction * seekInterval)));
          PlaybackStore.setCurrentTime(newTime);

          // Start exponential increase timer
          seekTimer = setInterval(() => {
            seekInterval += 5; // Increment by 5 seconds each interval
            const currentTime = audioElement.currentTime;
            const duration = audioElement.duration;
            const newTime = Math.max(0, Math.min(duration, currentTime + (direction * seekInterval)));
            PlaybackStore.setCurrentTime(newTime);
          }, 500); // Every 500ms while holding
        }
      }
    };

    const handleKeyUp = (e) => {
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        currentKey = null;
        if (seekTimer) {
          clearInterval(seekTimer);
          seekTimer = null;
        }
        seekInterval = 5; // Reset
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('keyup', handleKeyUp);

    return () => {
      clearInterval(recentUserInterval);
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('keyup', handleKeyUp);
      if (seekTimer) clearInterval(seekTimer);
    };
  }, [PlaylistStore, RecentUsersStore, PlaybackStore]);

  const handleTogglePlaylist = (value) => {
    if (typeof value === 'boolean') {
      setPlaylistVisible(value);
    } else {
      setPlaylistVisible(!playlistVisible);
    }
  };

  const handleScrollToCurrentTrack = () => {
    // Exit preview mode if active
    if (PlaylistStore.preview) {
      PlaylistStore.clear();
    }

    // Close drawer on mobile to reveal playlist
    if (playlistVisible) {
      setPlaylistVisible(false);
    }

    // Get current track ID
    const currentTrackId = PlaybackStore.currentTrackId;

    // Ensure playlist is not empty and track exists
    if (!currentTrackId || PlaybackStore.playlist.length === 0) {
      return;
    }

    // Delay scroll to allow for:
    // 1. Preview mode exit (state update)
    // 2. Drawer animation on mobile (300ms)
    // 3. DOM to update
    const scrollDelay = (playlistVisible || PlaylistStore.preview) ? 350 : 100;

    setTimeout(() => {
      if (playlistRef.current) {
        playlistRef.current.scrollToTrack(currentTrackId);
      }
    }, scrollDelay);
  };

  return (
    <div className="flex flex-col h-screen w-full overflow-hidden">
      {/* Desktop: left vertical rail */}
      <Rail />
      {/* Mobile: slim top bar (lg:hidden) */}
      <Header onTogglePlaylist={handleTogglePlaylist} playlistVisible={playlistVisible} />

      {/* Main Content Area — pads for mobile header and rail */}
      <div className={`flex flex-1 min-h-0 overflow-hidden w-full pt-14 lg:pt-0 lg:pl-14 ${djMode ? "pb-0" : "pb-[140px] md:pb-[80px]"}`}>


        {/* Content panel — hosts Home/Profile/Admin/DJ.
            Normal mode: drawer behavior on mobile (slides in over Playlist).
            DJ mode:     always in-flow, full width on mobile, 65% on desktop. */}
        <div
          className={
            djMode
              ? "relative w-full lg:w-[65%] overflow-y-auto thin-scroll h-full"
              : `fixed lg:relative top-14 lg:top-0 right-0 bottom-[140px] md:bottom-[80px] z-40 w-full sm:w-96 lg:w-[65%] overflow-y-auto thin-scroll transform transition-transform duration-300 ease-in-out ${playlistVisible ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}`
          }
        >
          <UploadContextProvider>
            <div style={{ display: djMode ? "none" : "block" }}>
              <Routes>
                <Route path="/" element={<Home onTogglePlaylist={handleTogglePlaylist} />} />
                <Route path="/me" element={<MyProfile onTogglePlaylist={handleTogglePlaylist} />} />
                <Route path="/u/:username" element={<UserProfile onBack={() => handleTogglePlaylist(false)} />} />
                <Route path="/admin" element={<Admin />} />
              </Routes>
            </div>
            <div style={{ display: djMode ? "block" : "none" }} className="h-full">
              <Dj />
            </div>
          </UploadContextProvider>

        </div>

        {/* Playlist panel.
            Normal mode: in-flow, full width on mobile, 35% on desktop.
            DJ mode:     drawer behavior on mobile (slides in over DJ). */}
        <div
          className={
            djMode
              ? `fixed lg:relative top-14 lg:top-0 right-0 bottom-0 z-40 w-full sm:w-96 lg:w-[35%] overflow-y-auto bg-primary transform transition-transform duration-300 ease-in-out ${playlistVisible ? 'translate-x-0' : 'translate-x-full lg:translate-x-0'}`
              : "w-full lg:w-[35%] overflow-y-auto"
          }
        >
          {djMode ? (
            <DjQueuePanel />
          ) : (
            <Playlist
              ref={playlistRef}
              playlist={
                PlaylistStore.preview
                  ? PlaylistStore.playlist
                  : PlaybackStore.playlist
              }
              preview={PlaylistStore.preview}
              clearPreview={() => {
                PlaylistStore.clear();
              }}
              selectedTrack={PlaybackStore.currentTrackId}
              onPlay={(trackId) => {
                stopDjAudio();
                if (PlaylistStore.preview) {
                  PlaybackStore.setPlaylist(PlaylistStore.playlist, PlaylistStore.currentViewingPlaylistId);
                  PlaylistStore.clear();
                }
                PlaybackStore.playTrack(trackId);
              }}
              onClose={() => setPlaylistVisible(false)}
              currentPlaylistId={PlaylistStore.currentViewingPlaylistId}
            />
          )}
        </div>

        {/* Overlay for mobile when drawer is open */}
        {playlistVisible && (
          <div
            className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
            onClick={() => setPlaylistVisible(false)}
          />
        )}
      </div>

      {/* Player Controls - Fixed to bottom (hidden in DJ mode) — offset for desktop rail */}
      {!djMode && (
        <div className="fixed bottom-0 left-0 lg:left-14 right-0 z-50">
          <Playback
            RecentUsersStore={RecentUsersStore}
            onScrollToCurrentTrack={handleScrollToCurrentTrack}
          />
        </div>
      )}

      {/* Add to Playlist Modal */}
      <AddToPlaylistModal />
    </div>
  );
}

export default inject(
  "PlaylistStore",
  "RecentUsersStore",
  "DjStore",
  "PlaybackStore"
)(observer(Main));
